import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import AdminLayout from "../../components/AdminLayout";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Label } from "../../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { FileDown, FileSpreadsheet, Truck, DollarSign, Loader2, CalendarClock } from "lucide-react";
import B2CReportsSection from "@/components/B2CReportsSection";
import DateRangePicker, { defaultRange } from "@/components/DateRangePicker";
import { SearchableMultiSelect } from "@/components/ui/searchable-multi-select";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

const DATE_FIELD_OPTIONS = [
  { value: "created_at", label: "Created At" },
  { value: "delivered_date", label: "Delivered Date" },
  { value: "manifested_date", label: "Manifested Date" },
  { value: "appointment_date", label: "Appointment Date" },
];

const APPT_DATE_FIELD_OPTIONS = [
  { value: "appointment_date", label: "Appointment Date" },
  { value: "created_at", label: "Created At" },
  { value: "manifested_date", label: "Manifested Date" },
];

// Module-level (stable identity) so the dropdown's open state survives re-renders
// when a seller is toggled — otherwise it would close after every selection.
const SellerMultiSelect = ({ sellers, loadingSellers, selectedSellers, setSelectedSellers }) => (
  <div className="space-y-1.5">
    <Label>Select Sellers (Optional)</Label>
    <SearchableMultiSelect
      values={selectedSellers}
      onChange={setSelectedSellers}
      options={sellers.map((s) => ({ value: s.id, label: s.name, searchLabel: s.name }))}
      placeholder={loadingSellers ? "Loading sellers…" : "All sellers (search to filter)"}
      emptyText={loadingSellers ? "Loading…" : "No sellers found."}
    />
    {selectedSellers.length > 0 && (
      <p className="text-xs text-slate-500">{selectedSellers.length} seller(s) selected</p>
    )}
  </div>
);

const AdminReports = ({ user, onLogout }) => {
  const [tab, setTab] = useState("b2c");
  const [sellers, setSellers] = useState([]);
  const [loadingSellers, setLoadingSellers] = useState(true);
  
  // LRN Detail Report state
  const [lrnDetailSellers, setLrnDetailSellers] = useState([]);
  const [lrnDetailDateField, setLrnDetailDateField] = useState("created_at");
  const [lrnDetailRange, setLrnDetailRange] = useState(() => defaultRange("this_month"));
  const [downloadingLrnDetail, setDownloadingLrnDetail] = useState(false);

  // LRN Charges Report state
  const [lrnChargesSellers, setLrnChargesSellers] = useState([]);
  const [lrnChargesRange, setLrnChargesRange] = useState(() => defaultRange("this_month"));
  const [downloadingLrnCharges, setDownloadingLrnCharges] = useState(false);

  // Freight by Partner Report state
  const [freightRange, setFreightRange] = useState(() => defaultRange("this_month"));
  const [downloadingFreight, setDownloadingFreight] = useState(false);

  // Appointment LRN Report state
  const [apptSellers, setApptSellers] = useState([]);
  const [apptDateField, setApptDateField] = useState("appointment_date");
  const [apptRange, setApptRange] = useState(() => defaultRange("this_month"));
  const [downloadingAppt, setDownloadingAppt] = useState(false);

  useEffect(() => {
    fetchSellers();
  }, []);

  const fetchSellers = async () => {
    try {
      const response = await axios.get(`${API}/reports/sellers-list`);
      setSellers(response.data);
    } catch (error) {
      console.error("Error fetching sellers:", error);
      toast.error("Failed to load sellers list");
    } finally {
      setLoadingSellers(false);
    }
  };

  const downloadReport = async (endpoint, params, setDownloading, filename) => {
    setDownloading(true);
    try {
      const queryParams = new URLSearchParams();
      Object.entries(params).forEach(([key, value]) => {
        if (value) queryParams.append(key, value);
      });
      
      const response = await axios.get(`${API}/reports/${endpoint}?${queryParams.toString()}`, {
        responseType: 'blob'
      });
      
      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success("Report downloaded successfully!");
    } catch (error) {
      console.error("Error downloading report:", error);
      toast.error("Failed to download report");
    } finally {
      setDownloading(false);
    }
  };

  const handleDownloadLrnDetail = () => {
    const params = {
      seller_ids: lrnDetailSellers.join(","),
      date_field: lrnDetailDateField,
      start_date: lrnDetailRange.start,
      end_date: lrnDetailRange.end
    };
    downloadReport("lrn-detail", params, setDownloadingLrnDetail, `lrn_detail_report_${new Date().toISOString().split('T')[0]}.csv`);
  };

  const handleDownloadLrnCharges = () => {
    const params = {
      seller_ids: lrnChargesSellers.join(","),
      start_date: lrnChargesRange.start,
      end_date: lrnChargesRange.end
    };
    downloadReport("lrn-charges", params, setDownloadingLrnCharges, `lrn_charges_report_${new Date().toISOString().split('T')[0]}.csv`);
  };

  const handleDownloadFreight = () => {
    const params = {
      start_date: freightRange.start,
      end_date: freightRange.end
    };
    downloadReport("freight-by-partner", params, setDownloadingFreight, `freight_by_partner_report_${new Date().toISOString().split('T')[0]}.csv`);
  };

  const handleDownloadAppointmentLrn = () => {
    const params = {
      seller_ids: apptSellers.join(","),
      date_field: apptDateField,
      start_date: apptRange.start,
      end_date: apptRange.end
    };
    downloadReport("appointment-lrn", params, setDownloadingAppt, `appointment_lrn_report_${new Date().toISOString().split('T')[0]}.csv`);
  };

  return (
    <AdminLayout user={user} onLogout={onLogout}>
      <div className="space-y-6" data-testid="admin-reports-page">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Reports</h1>
          <p className="text-slate-600">Generate and download CSV reports across sellers</p>
        </div>

        {/* B2C / B2B tabs */}
        <div className="flex gap-2 border-b border-slate-200">
          {[["b2c", "B2C Last Mile"], ["b2b", "B2B Freight"]].map(([id, label]) => (
            <button
              key={id}
              onClick={() => setTab(id)}
              data-testid={`reports-tab-${id}`}
              className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors ${
                tab === id ? "border-brand text-brand" : "border-transparent text-slate-600 hover:text-slate-900"
              }`}
            >
              {label}
            </button>
          ))}
        </div>

        {tab === "b2c" ? (
          <B2CReportsSection />
        ) : (
        <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
          {/* LRN Detail Report */}
          <Card className="border-slate-200">
            <CardHeader>
              <div className="flex items-center gap-2">
                <FileSpreadsheet className="w-5 h-5 text-blue-600" />
                <CardTitle className="text-lg">LRN Detail Report</CardTitle>
              </div>
              <CardDescription>
                Export detailed information for all orders including LRN, consignee, pickup &amp; drop details, charged weight, box count, shipping info, and dates.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <SellerMultiSelect
                sellers={sellers}
                loadingSellers={loadingSellers}
                selectedSellers={lrnDetailSellers}
                setSelectedSellers={setLrnDetailSellers}
              />
              
              <div>
                <Label>Date Field</Label>
                <Select value={lrnDetailDateField} onValueChange={setLrnDetailDateField}>
                  <SelectTrigger className="mt-1" data-testid="lrn-detail-date-field">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {DATE_FIELD_OPTIONS.map(opt => (
                      <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <div>
                <Label>Date range</Label>
                <div className="mt-1">
                  <DateRangePicker value={lrnDetailRange} onChange={setLrnDetailRange} />
                </div>
              </div>

              <Button
                onClick={handleDownloadLrnDetail}
                disabled={downloadingLrnDetail || !lrnDetailRange.start || !lrnDetailRange.end}
                className="w-full"
                style={{ backgroundColor: (!lrnDetailRange.start || !lrnDetailRange.end) ? '#94a3b8' : '#00A699' }}
                data-testid="download-lrn-detail-btn"
              >
                {downloadingLrnDetail ? (
                  <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Generating...</>
                ) : (
                  <><FileDown className="w-4 h-4 mr-2" /> Download</>
                )}
              </Button>
            </CardContent>
          </Card>

          {/* LRN Charges Report */}
          <Card className="border-slate-200">
            <CardHeader>
              <div className="flex items-center gap-2">
                <DollarSign className="w-5 h-5 text-green-600" />
                <CardTitle className="text-lg">LRN Charges Report</CardTitle>
              </div>
              <CardDescription>
                Export charges breakdown for each order including freight, COD, handling, and appointment charges.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <SellerMultiSelect
                sellers={sellers}
                loadingSellers={loadingSellers}
                selectedSellers={lrnChargesSellers}
                setSelectedSellers={setLrnChargesSellers}
              />
              
              <div>
                <Label>Date range</Label>
                <div className="mt-1">
                  <DateRangePicker value={lrnChargesRange} onChange={setLrnChargesRange} />
                </div>
              </div>

              <Button
                onClick={handleDownloadLrnCharges}
                disabled={downloadingLrnCharges || !lrnChargesRange.start || !lrnChargesRange.end}
                className="w-full"
                style={{ backgroundColor: (!lrnChargesRange.start || !lrnChargesRange.end) ? '#94a3b8' : '#00A699' }}
                data-testid="download-lrn-charges-btn"
              >
                {downloadingLrnCharges ? (
                  <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Generating...</>
                ) : (
                  <><FileDown className="w-4 h-4 mr-2" /> Download</>
                )}
              </Button>
            </CardContent>
          </Card>

          {/* Freight by Partner Report */}
          <Card className="border-slate-200">
            <CardHeader>
              <div className="flex items-center gap-2">
                <Truck className="w-5 h-5 text-orange-600" />
                <CardTitle className="text-lg">Freight by Partner</CardTitle>
              </div>
              <CardDescription>
                Export freight charges breakdown by shipping partner including base freight, COD, fuel surcharge, etc.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="p-4 bg-slate-50 rounded-md">
                <p className="text-sm text-slate-600">
                  This report includes all orders with freight charges from shipping partners.
                </p>
              </div>
              
              <div>
                <Label>Date range</Label>
                <div className="mt-1">
                  <DateRangePicker value={freightRange} onChange={setFreightRange} />
                </div>
              </div>

              <Button
                onClick={handleDownloadFreight}
                disabled={downloadingFreight || !freightRange.start || !freightRange.end}
                className="w-full"
                style={{ backgroundColor: (!freightRange.start || !freightRange.end) ? '#94a3b8' : '#00A699' }}
                data-testid="download-freight-btn"
              >
                {downloadingFreight ? (
                  <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Generating...</>
                ) : (
                  <><FileDown className="w-4 h-4 mr-2" /> Download</>
                )}
              </Button>
            </CardContent>
          </Card>

          {/* Appointment LRN Report */}
          <Card className="border-slate-200">
            <CardHeader>
              <div className="flex items-center gap-2">
                <CalendarClock className="w-5 h-5 text-indigo-600" />
                <CardTitle className="text-lg">Appointment LRN Report</CardTitle>
              </div>
              <CardDescription>
                Export every LRN with its appointment date &amp; time and Delhivery appointment-booking status (booked / not booked), plus consignee, partner, and PO expiry.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <SellerMultiSelect
                sellers={sellers}
                loadingSellers={loadingSellers}
                selectedSellers={apptSellers}
                setSelectedSellers={setApptSellers}
              />

              <div>
                <Label>Date Field</Label>
                <Select value={apptDateField} onValueChange={setApptDateField}>
                  <SelectTrigger className="mt-1" data-testid="appt-lrn-date-field">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {APPT_DATE_FIELD_OPTIONS.map(opt => (
                      <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label>Date range</Label>
                <div className="mt-1">
                  <DateRangePicker value={apptRange} onChange={setApptRange} />
                </div>
              </div>

              <Button
                onClick={handleDownloadAppointmentLrn}
                disabled={downloadingAppt || !apptRange.start || !apptRange.end}
                className="w-full"
                style={{ backgroundColor: (!apptRange.start || !apptRange.end) ? '#94a3b8' : '#00A699' }}
                data-testid="download-appt-lrn-btn"
              >
                {downloadingAppt ? (
                  <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Generating...</>
                ) : (
                  <><FileDown className="w-4 h-4 mr-2" /> Download</>
                )}
              </Button>
            </CardContent>
          </Card>
        </div>
        )}
      </div>
    </AdminLayout>
  );
};

export default AdminReports;
